import {
  ActivityTone,
  BookTimeZone,
  CallSheetSort,
  CommitmentClosedReason,
  EnrichmentRunState,
  EnrichmentStageStatus,
  MatchDisposition,
  MatchStage,
  ProspectBookFilter,
  ProspectBookSort,
  ProspectMatchStatus,
  SortDirection,
} from '../generated/enums.gen';
import type { AcceptedRecordDto, CallSheetDto, DonorTimelineItemDto, OpenCommitmentSummaryDto, ProspectCallBookDto, ProspectDto, ProspectWorkLineDto } from '../generated/dtos.gen';

// Fundraising / Call Time — the read shapes the Enrichment results preview renders.
//
// These mirror the backend read records served by `IEnrichmentMatchReader`
// (apps/api/Features/Fundraising/Enrichment/EnrichmentMatchReader.cs +
// apps/api/Features/Fundraising/Corpus/ICorpusReader.cs) VERBATIM — the endpoint has no DTO of its
// own, it returns the reader's records directly. The page reads through this contract, so the shapes
// here are the contract, not a storage model. Money is a `number`, dates are ISO `yyyy-MM-dd` strings,
// and enum values are the backend PascalCase names (`JsonStringEnumConverter`).

/**
 * One committee × cycle entry of a Corpus Identity's Giving History rollup — itemized only (federal's
 * $200 disclosure threshold) and gross of refunds. Not a list of individual gifts.
 */
export interface GivingHistoryEntry {
  committeeId: string;
  /** LEFT-joined from FEC's committee master; null when the committee is absent from it. */
  committeeName: string | null;
  cycle: number;
  /** Sum of counted gifts for this committee × cycle, gross of refunds. */
  totalAmount: number;
  giftCount: number;
  /** The largest single itemized gift — the capacity signal the Ask reads (Slice 6). */
  maxAmount: number;
  firstDate: string | null;
  lastDate: string | null;
}

/**
 * One match as the reader surfaces it — the keyless Corpus Identity snapshot (locator + carried
 * descriptors) with its Giving History rollup and the disposition band. Never a score.
 */
export interface MatchRead {
  matchId: string;
  disposition: MatchDisposition;

  // Keyless Corpus Identity snapshot (locator + carried descriptors), exactly as persisted.
  givenName: string;
  lastCore: string;
  suffix: string;
  streetFolded: string;
  zip5: string;
  middle: string;
  city: string;
  state: string;
  address2: string;
  employer: string;
  occupation: string;

  /** This identity's committee × cycle Giving History rollup (empty, never null). */
  giving: GivingHistoryEntry[];
}

/**
 * One enriched person's derived reads, computed at read from their disposable match set and durable
 * verdicts — never stored. The three sets are disjoint surfaces: `accepted` is the union that sizes
 * the Ask, `review` is the read-only tail, `suppressed` is the blocked band. `acceptedGiving` is the
 * union of the accepted matches' rollups.
 */
export interface EnrichedPersonRead {
  prospectId: string;
  /** The live Corpus Version string the matches were derived at — a freshness marker. */
  derivedAtVersion: string;
  accepted: MatchRead[];
  review: MatchRead[];
  suppressed: MatchRead[];
  /** The union of the accepted matches' rollups; empty when nothing is accepted. */
  acceptedGiving: GivingHistoryEntry[];
}

// --- Follow-ups (GET /api/fundraising/call-time/follow-up-drafts,
//     POST /api/fundraising/call-time/attempts/{id}/follow-ups) ---
//
// After a call, the Caller hands a drafted text or email to the phone's own Messages or Mail composer,
// so it goes out from their personal number or account. We record the hand-off — the draft offered and
// what the phone reported — never the exact text that went out. Mirrors the backend `FollowUp` records.

// --- Open commitments (GET /api/fundraising/commitments, POST .../{id}/close, Fundraising: Manage) ---

/** The reasons a Fundraiser can pick when closing a Commitment by hand. */
export const FUNDRAISER_CLOSE_REASONS = [
  CommitmentClosedReason.ReceivedElsewhere,
  CommitmentClosedReason.Withdrawn,
  CommitmentClosedReason.Lapsed,
] as const;

/** The longest a Prospect's note may be — the server's `Prospects.MaxNotesLength`. */
export const PROSPECT_NOTES_MAX_LENGTH = 2000;

/** How close to the limit a note editor starts counting down the characters left (web and phone alike). */
export const PROSPECT_NOTES_COUNT_DOWN_FROM = 200;

// --- The Prospects book (GET /api/fundraising/prospects/book?activity=&match=&sheet=&search=) ---

/** The chips the activity filter takes. */
export type ProspectBookActivity =
  | ProspectBookFilter.NeverCalled
  | ProspectBookFilter.CallBackOwed
  | ProspectBookFilter.OpenCommitment;

/** The chips the Match filter takes — what the book knows of a person, No phone included. */
export type ProspectBookMatch =
  | ProspectBookFilter.FederalGiving
  | ProspectBookFilter.PossibleMatch
  | ProspectBookFilter.NoneFound
  | ProspectBookFilter.NotMatchedYet
  | ProspectBookFilter.NoPhone;

/** The book's filters, all of which can be on at once. Null means any. */
export interface ProspectBookQuery {
  activity?: ProspectBookActivity | null;
  match?: ProspectBookMatch | null;
  /** A call book's id: its people, in its fixed order, with its progress. */
  sheetId?: string | null;
  search?: string;
  /** An uploaded list's id: the people it brought in, whichever other lists did too. */
  listId?: string | null;
  /** A state, by its code or its name. */
  state?: string | null;
  /** A city, ignoring case and spaces. */
  city?: string;
  /** The start of a ZIP: "303" finds 30301 and 30305-1234. */
  zip?: string;
  /** The Ask range, inclusive, read from the Ask as shown (held to the ceiling). Either end set leaves out
   *  people with no Ask. */
  minAsk?: number | null;
  maxAsk?: number | null;
  /** A time zone read from each person's address; `Unknown` finds those whose address doesn't say. */
  timeZone?: BookTimeZone | null;
  /** The column the server sorts the whole filtered set by, before the cap; null is largest Ask first. */
  sort?: ProspectBookSort | null;
  /** Which way; null with a column is ascending. */
  dir?: SortDirection | null;
}

/** The caption under a work field the server filled from their FEC record, on the web and the phone. */
export const FEC_WORK_LINE_CAPTION = 'From FEC record';
